import React from 'react'

import type {LexicalEditor} from 'lexical'
import {
    AutoEmbedOption,
    EmbedConfig,
    EmbedMatchResult,
    LexicalAutoEmbedPlugin,
} from '@lexical/react/LexicalAutoEmbedPlugin'
import {INSERT_YOUTUBE_COMMAND} from "./youtube"
import {INSERT_FIGMA_COMMAND} from "./figma"
import {EmbedMenu} from "./menu"


interface EditorEmbedConfig extends EmbedConfig {
    // Human readable name of the embeded content e.g. Tweet or Google Map.
    contentName : string

    // An example of a matching url https://twitter.com/jack/status/20
    exampleUrl  : string

    // For extra searching.
    keywords    : Array<string>

    // Embed a Figma Project.
    description?: string
}

export const YoutubeEmbedConfig: EditorEmbedConfig = {
    contentName : 'Youtube Video',
    exampleUrl  : 'https://www.youtube.com/watch?v=',

    insertNode  : (editor: LexicalEditor, result: EmbedMatchResult) => {
        editor.dispatchCommand(INSERT_YOUTUBE_COMMAND, result.id);
    },

    keywords: ['youtube', 'video'],

    // Determine if a given URL is a match and return url data.
    parseUrl: async (url: string): Promise<{id:string, url:string} | null> => {
        const match =
            /^.*(youtu\.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/.exec(url)

        const id = match ? (match?.[2].length === 11 ? match[2] : null) : null

        if (id != null) {
            return {
                id,
                url,
            };
        }

        return null
    },

    type: 'youtube-video',
};

export const FigmaEmbedConfig: EditorEmbedConfig = {
    contentName : 'Figma Document',
    exampleUrl  : 'https://www.figma.com/file/',

    insertNode  : (editor: LexicalEditor, result: EmbedMatchResult) => {
        editor.dispatchCommand(INSERT_FIGMA_COMMAND, result.id);
    },

    keywords    : ['figma', 'figma.com', 'mock-up'],

    // Determine if a given URL is a match and return url data.
    parseUrl    : (text: string): {id:string, url:string} | null => {
        const match =
            /https:\/\/([\w.-]+\.)?figma.com\/(file|proto)\/([0-9a-zA-Z]{22,128})(?:\/.*)?$/.exec(text)

        if (match != null) {
            return {
                id: match[3],
                url: match[0],
            }
        }

        return null
    },

    type: 'figma',
}

export const EmbedConfigs = [
    YoutubeEmbedConfig,
    FigmaEmbedConfig,
]

export default function AutoEmbedPlugin(): JSX.Element {

    const getMenuOptions = (
        activeEmbedConfig   : EditorEmbedConfig,
        embedFn             : () => void,
        dismissFn           : () => void,
    ) => {
        return [
            new AutoEmbedOption('Dismiss', {
                onSelect: dismissFn,
            }),
            new AutoEmbedOption(`Embed ${activeEmbedConfig.contentName}`, {
                onSelect: embedFn,
            }),
        ]
    }

    return (
        <LexicalAutoEmbedPlugin<EditorEmbedConfig> embedConfigs                 = {EmbedConfigs}
                                                   onOpenEmbedModalForConfig    = {()=>{}}
                                                   getMenuOptions               = {getMenuOptions}
                                                   menuRenderFn                 = {EmbedMenu}/>
    )
}
